import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Phone, MapPin, Award, CalendarDays, Dog, Percent, StickyNote, TrendingUp } from "lucide-react";

export default function TrainerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/trainers/${id}/detail`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="text-center py-20 text-muted-foreground">Trainer not found.</div>;
  if (!d) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const t = d.trainer;
  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/trainers")} data-testid="trainer-back"><ArrowLeft size={16} /> Back to Trainers</Button>

      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t.full_name}</h1>
          <div className="flex flex-wrap gap-4 mt-1.5 text-sm text-muted-foreground">
            {t.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {t.email}</span>}
            {t.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {t.phone}</span>}
            {t.location_name && <span className="flex items-center gap-1.5"><MapPin size={13} /> {t.location_name}</span>}
          </div>
          {t.specialties && <div className="text-sm mt-1.5">{t.specialties}</div>}
        </div>
        <div className="flex items-center gap-2"><StatusBadge value={t.certification_status} /><StatusBadge value={t.status} /></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { icon: CalendarDays, label: "Classes", value: d.classes.length },
          { icon: Dog, label: "Dogs", value: d.dogs.length },
          { icon: Percent, label: "Attendance", value: `${t.attendance_rate ?? 0}%` },
          { icon: Award, label: "Certification", value: (t.certification_status || "").replace(/_/g, " ") },
        ].map((k) => (
          <Card key={k.label} className="border-border" data-testid={`trainer-kpi-${k.label.toLowerCase()}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1.5"><k.icon size={14} strokeWidth={1.5} /><span className="text-[10px] uppercase tracking-wide font-semibold">{k.label}</span></div>
              <div className="text-lg font-heading font-semibold capitalize">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><CalendarDays size={18} strokeWidth={1.5} /> Assigned Classes ({d.classes.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {d.classes.length ? d.classes.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60" data-testid="trainer-class">
                  <div><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-muted-foreground">{fmtDate(c.date)} · {c.time}</div></div>
                  <StatusBadge value={c.status} />
                </div>
              )) : <p className="text-sm text-muted-foreground">No assigned classes.</p>}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Dog size={18} strokeWidth={1.5} /> Assigned Dogs ({d.dogs.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {d.dogs.length ? d.dogs.map((dog) => (
                <div key={dog.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60" data-testid="trainer-dog">
                  <div><div className="font-medium text-sm">{dog.name}</div><div className="text-xs text-muted-foreground">{dog.breed} · {dog.client_name}</div></div>
                  <StatusBadge value={dog.current_level} />
                </div>
              )) : <p className="text-sm text-muted-foreground">No assigned dogs.</p>}
            </CardContent>
          </Card>
        </div>
        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><TrendingUp size={18} strokeWidth={1.5} /> Performance</CardTitle></CardHeader>
            <CardContent><div className="p-3 rounded-lg border border-dashed border-border text-sm text-muted-foreground">Detailed performance scoring coming with HV Quality.</div></CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><StickyNote size={18} strokeWidth={1.5} /> Notes</CardTitle></CardHeader>
            <CardContent><div className="p-3 rounded-lg border border-dashed border-border text-sm text-muted-foreground">Trainer notes — coming soon.</div></CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
